import { useMutation } from '@apollo/client';

import type { Maybe } from '@xing-com/crate-common-graphql-types';

import { ArticleFollowContentPageDocument } from '../graphql/follow-mutation.gql-types';
import type {
  ArticleFollowContentPageMutation,
  ArticleFollowContentPageMutationVariables,
} from '../graphql/follow-mutation.gql-types';
import { ArticleUnfollowContentPageDocument } from '../graphql/unfollow-mutation.gql-types';
import type {
  ArticleUnfollowContentPageMutation,
  ArticleUnfollowContentPageMutationVariables,
} from '../graphql/unfollow-mutation.gql-types';
import type { ArticleAuthorType } from '../types';

type UseFollowUnfollowResult = {
  followAuthor: () => void;
  unfollowAuthor: () => void;
  loading: boolean;
  error: boolean;
};

export const useFollowUnfollow = (
  author: Maybe<ArticleAuthorType>,
  config?: { onCompleted?: (isFollowed: boolean) => void; onError?: () => void }
): UseFollowUnfollowResult => {
  const [followAuthor, { loading: followLoading, error: followError }] =
    useMutation<
      ArticleFollowContentPageMutation,
      ArticleFollowContentPageMutationVariables
    >(ArticleFollowContentPageDocument, {
      errorPolicy: 'all',
      variables: {
        input: {
          pageId: author?.id || '',
        },
      },
      onCompleted: (data) => {
        if (data.contentPageFollow?.success) {
          config?.onCompleted?.(
            data.contentPageFollow.success.interactions?.isFollowed ?? false
          );
        } else if (data.contentPageFollow?.error) {
          config?.onError?.();
        }
      },
      onError: () => config?.onError?.(),
    });

  const [unfollowAuthor, { loading: unfollowLoading, error: unfollowError }] =
    useMutation<
      ArticleUnfollowContentPageMutation,
      ArticleUnfollowContentPageMutationVariables
    >(ArticleUnfollowContentPageDocument, {
      errorPolicy: 'all',
      variables: {
        input: {
          pageId: author?.id || '',
        },
      },
      onCompleted: (data) => {
        if (data.contentPageUnfollow?.success) {
          config?.onCompleted?.(
            data.contentPageUnfollow.success.interactions?.isFollowed ?? true
          );
        } else if (data.contentPageUnfollow?.error) {
          config?.onError?.();
        }
      },
      onError: () => config?.onError?.(),
    });

  return {
    followAuthor,
    unfollowAuthor,
    loading: followLoading || unfollowLoading,
    error: !!followError || !!unfollowError,
  };
};
